import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { SAT_QUESTIONS, pl } from "../lib/partnerConstants";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Star, CheckCircle2, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function Stars({ value, onChange, testid }) {
  return (
    <div className="flex gap-1.5" data-testid={testid}>
      {[1, 2, 3, 4, 5].map((n) => (
        <button key={n} type="button" onClick={() => onChange(n)} data-testid={`${testid}-${n}`} className="transition hover:scale-110">
          <Star className="h-7 w-7" style={{ fill: n <= value ? "#f59e0b" : "transparent", color: n <= value ? "#f59e0b" : "#cbd5e1" }} />
        </button>
      ))}
    </div>
  );
}

export default function SatisfactionPublic() {
  const { token } = useParams();
  const { i18n } = useTranslation();
  const lang = i18n.language || "fr";
  const [state, setState] = useState("loading"); // loading / form / done / responded / error
  const [partner, setPartner] = useState("");
  const [scores, setScores] = useState({});
  const [text, setText] = useState({ comment: "", positives: "", improvements: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/public/satisfaction/${token}`);
        setPartner(r.data.partner_name || "");
        if (r.data.responded) setState("responded");
        else setState("form");
      } catch { setState("error"); }
    })();
  }, [token]);

  const submit = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/public/satisfaction/${token}`, { ...scores, ...text });
      setState("done");
    } catch { setState("error"); } finally { setBusy(false); }
  };

  const allRated = SAT_QUESTIONS.every((q) => (scores[q.k] || 0) > 0);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-emerald-950 to-slate-900 flex items-center justify-center p-4" data-testid="sat-public-page">
      <div className="w-full max-w-xl rounded-3xl border border-white/10 bg-white/[0.04] backdrop-blur-xl p-6 sm:p-8 shadow-2xl">
        <div className="text-center mb-6">
          <div className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-emerald-500/20 mb-3"><Star className="h-6 w-6 text-emerald-400" /></div>
          <h1 className="font-display text-2xl font-bold text-white">Votre satisfaction</h1>
          <p className="text-sm text-emerald-200/70 mt-1">Groupe Novadia{partner ? ` — ${partner}` : ""}</p>
        </div>

        {state === "loading" && <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-emerald-400" /></div>}
        {state === "error" && <p className="text-center text-rose-300 py-8" data-testid="sat-public-error">Ce lien est invalide ou a expiré.</p>}
        {state === "responded" && <p className="text-center text-emerald-200 py-8" data-testid="sat-public-responded">Cette enquête a déjà reçu votre réponse. Merci !</p>}
        {state === "done" && (
          <div className="text-center py-8" data-testid="sat-public-done">
            <CheckCircle2 className="h-14 w-14 text-emerald-400 mx-auto mb-3" />
            <p className="text-lg font-semibold text-white">Merci pour votre retour&nbsp;!</p>
            <p className="text-sm text-emerald-200/70 mt-1">Votre avis nous aide à améliorer notre collaboration.</p>
          </div>
        )}

        {state === "form" && (
          <div className="space-y-5">
            <div className="space-y-4">
              {SAT_QUESTIONS.map((q) => (
                <div key={q.k} className="flex items-center justify-between gap-3 flex-wrap">
                  <span className="text-sm text-white/85">{pl(lang, q)}</span>
                  <Stars value={scores[q.k] || 0} onChange={(n) => setScores({ ...scores, [q.k]: n })} testid={`sat-pub-${q.k}`} />
                </div>
              ))}
            </div>
            <div className="space-y-3">
              <Textarea placeholder="Points positifs" value={text.positives} onChange={(e) => setText({ ...text, positives: e.target.value })} rows={2} data-testid="sat-pub-positives" />
              <Textarea placeholder="Améliorations souhaitées" value={text.improvements} onChange={(e) => setText({ ...text, improvements: e.target.value })} rows={2} data-testid="sat-pub-improvements" />
              <Textarea placeholder="Commentaire libre" value={text.comment} onChange={(e) => setText({ ...text, comment: e.target.value })} rows={2} data-testid="sat-pub-comment" />
            </div>
            <Button onClick={submit} disabled={!allRated || busy} className="w-full bg-emerald-600 hover:bg-emerald-500 h-11" data-testid="sat-pub-submit">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Envoyer ma réponse"}
            </Button>
            {!allRated && <p className="text-center text-xs text-amber-300/80">Merci de noter les 8 critères.</p>}
          </div>
        )}
      </div>
    </div>
  );
}
